import { GeocodableAddress } from "./Address";
import { SimpleHorse } from "./Horse";
import { SimpleCustomer, Customer, InsuranceRecord, CreditApplication, OnTheSpotCreditApplication, AgentAndDelegatorAuthorization } from "./Customer";
import { AttachmentLog, ComplianceAttachmentLog } from "./Attachment";
import { Invoice } from "./Financial";

export enum HorseForSaleState {
    APPLICATION = "APPLICATION",
    SCHEDULED = "SCHEDULED",
    APPROVED = "APPROVED",
    APPROVED_FOR_SALE = "APPROVED_FOR_SALE",
    REJECTED = "REJECTED",
    BID_IN = "BID_IN",
    PROVISIONALLY_SOLD = "PROVISIONALLY_SOLD",
    SOLD = "SOLD",
    WITHDRAWN_WITH_PENALTY_GENERATED = "WITHDRAWN_WITH_PENALTY_GENERATED",
    WITHDRAWN_WITH_PENALTY_PENDING = "WITHDRAWN_WITH_PENALTY_PENDING",
    WITHDRAWN_WITHOUT_PENALTY = "WITHDRAWN_WITHOUT_PENALTY",
    WITHDRAWN_EARLY = "WITHDRAWN_EARLY"
}

export const HorseForSaleStateFriendlyName = (state: HorseForSaleState): string => {
    switch (state) {
        case HorseForSaleState.APPLICATION:
            return "Application";
        case HorseForSaleState.SCHEDULED:
            return "Scheduled";
        case HorseForSaleState.APPROVED:
            return "Approved, Pending Details";
        case HorseForSaleState.APPROVED_FOR_SALE:
            return "Approved, Ready for Sale";
        case HorseForSaleState.REJECTED:
            return "Rejected";
        case HorseForSaleState.BID_IN:
            return "Bid In";
        case HorseForSaleState.PROVISIONALLY_SOLD:
            return "Provisionally Sold";
        case HorseForSaleState.SOLD:
            return "Sold";
        case HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED:
            return "Withdrawn with Penalty Generated";
        case HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING:
            return "Withdrawn with Penalty Pending";
        case HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY:
            return "Withdrawn without Penalty";
        case HorseForSaleState.WITHDRAWN_EARLY:
            return "Withdrawn Early";
        default:
            return "?";
    }
};

export const PreDecisionActiveStates = (): Array<HorseForSaleState> => {
    return [HorseForSaleState.APPLICATION, HorseForSaleState.SCHEDULED];
};

export const PreHipNumberActiveStates = (): Array<HorseForSaleState> => {
    return [HorseForSaleState.APPLICATION, HorseForSaleState.SCHEDULED, HorseForSaleState.APPROVED, HorseForSaleState.APPROVED_FOR_SALE];
};

export const PostApprovalWithdrawnStates = (): Array<HorseForSaleState> => {
    return [
        HorseForSaleState.WITHDRAWN_WITH_PENALTY_GENERATED,
        HorseForSaleState.WITHDRAWN_WITH_PENALTY_PENDING,
        HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY
    ];
};

export const AllWithdrawnStates = (): Array<HorseForSaleState> => {
    return [...PostApprovalWithdrawnStates(), HorseForSaleState.WITHDRAWN_EARLY];
};

export const ApprovedActiveStates = (): Array<HorseForSaleState> => {
    return [
        HorseForSaleState.APPROVED,
        HorseForSaleState.APPROVED_FOR_SALE,
        HorseForSaleState.BID_IN,
        HorseForSaleState.PROVISIONALLY_SOLD,
        HorseForSaleState.SOLD
    ];
};

export const CheckoutActiveStates = (state: HorseForSaleState): boolean => {
    switch (state) {
        case HorseForSaleState.BID_IN:
        case HorseForSaleState.PROVISIONALLY_SOLD:
        case HorseForSaleState.SOLD:
            return true;
        default:
            return false;
    }
};

export const AllPostApprovedStates = (): Array<HorseForSaleState> => {
    return [...ApprovedActiveStates(), ...PostApprovalWithdrawnStates()];
};

export const HorseForSaleStateOptions = () =>
    Object.keys(HorseForSaleState).map(state => {
        return {
            label: HorseForSaleStateFriendlyName(state as HorseForSaleState),
            value: state
        };
    });

export enum PreFilledFormsTypes {
    INSPECTION_FORM = "INSPECTION_FORM",
    ACKNOWLEDGEMENT_OF_PURCHASE_AND_ACCEPTANCE_OF_CONDITIONS_FORM = "ACKNOWLEDGEMENT_OF_PURCHASE_AND_ACCEPTANCE_OF_CONDITIONS_FORM",
    STABLE_RELEASE = "STABLE_RELEASE"
}

export const PreFilledFormsFriendlyName = (formName: PreFilledFormsTypes): string => {
    switch (formName) {
        case PreFilledFormsTypes.INSPECTION_FORM:
            return "Inspection Form";
        case PreFilledFormsTypes.ACKNOWLEDGEMENT_OF_PURCHASE_AND_ACCEPTANCE_OF_CONDITIONS_FORM:
            return "Acknowledgement of Purchase and Acceptance of Conditions Form";
        case PreFilledFormsTypes.STABLE_RELEASE:
            return "Stable Release";
        default:
            return "?";
    }
};

export const PreFilledFormsFiles = (formName: PreFilledFormsTypes): string => {
    switch (formName) {
        case PreFilledFormsTypes.INSPECTION_FORM:
            return "Inspection_Form";
        case PreFilledFormsTypes.ACKNOWLEDGEMENT_OF_PURCHASE_AND_ACCEPTANCE_OF_CONDITIONS_FORM:
            return "Acknowledgement_of_Purchase_and_Acceptance_of_Conditions_Form";
        case PreFilledFormsTypes.STABLE_RELEASE:
            return "Stable_Release";
        default:
            return "?";
    }
};

export enum Signatory {
    OWNER = "OWNER",
    AGENT = "AGENT"
}

export enum HSTExemptionType {
    INTL_SHIP = "INTL_SHIP",
    FN = "FN",
    NONE = "NONE"
}

export interface StakeEngagement {
    id: number;
    name: string;
    currentFrom: number;
    currentTo: number;
}

export interface SimpleHorseSale {
    id: number;
    name: string;
    current: boolean;
    currentFrom: number;
    currentTo: number;
    applicationsOpen: number;
    applicationsClose: number;
    hipNumbersGenerated: number | null;
}

export interface SaleDate {
    id: number;
    date: string;
}

export interface HorseSale extends SimpleHorseSale {
    dates: Array<SaleDate>;
    stakeEngagements: Array<StakeEngagement>;
    registeredInspectionDates: Array<string>;

    registrationFeeAmount: number;
    commissionFeeRate: number;
    commissionFeeMinimum: number;
    cadToUsdExchangeRate: number;
}

export interface HorseForSaleOwnership {
    owner: SimpleCustomer;
    percentage: number;
    cogginsInvoice: Invoice | null;
    commissionInvoice: Invoice | null;
    entryFeeInvoice: Invoice | null;
    registrationInvoice: Invoice | null;
}

export interface SimpleHorseForSale {
    id: number;
    state: HorseForSaleState;
    horse: SimpleHorse;
    sale: SimpleHorseSale;

    contact: SimpleCustomer | null;
    consignor: SimpleCustomer | null;

    inspectionDate: number | null;
    saleDate: SaleDate | null;
    hipNumber: number | null;
    spotInAgriplex: string | null;
    price: number | null;

    referenced: boolean;
}

export interface GeocodedHorseForSale extends SimpleHorseForSale {
    address911: GeocodableAddress;
}

export interface HorseForSale extends SimpleHorseForSale {
    owners: Array<HorseForSaleOwnership>;
    consignorCatalogName: string | null;
    raisedBy: SimpleCustomer | null;
    raisedByCatalogName: string | null;
    address911: GeocodableAddress | null;
    signatureOf: Signatory | null;
    signatureContact: SimpleCustomer | null;
    enrollmentInFullCircle: boolean;

    attachments: HorseForSaleAttachments;

    inspectionNotes: string | null;
    approvalNotes: string | null;
    earlyWithdrawalNotes: string | null;

    showOnPublicSalesReport: boolean;
    isCompliant: boolean;
    stakeEngagements: Array<StakeEngagement>;

    outcome: HorseForSaleOutcome | null;
}

export interface HorseForSaleAttachments {
    applicationFiles: AttachmentLog;
    inspectionFiles: AttachmentLog;
    hammerFallFiles: AttachmentLog;
    registrationCertificate: ComplianceAttachmentLog;
    cogginsCertificate: ComplianceAttachmentLog;
    stakeEngagements: ComplianceAttachmentLog;
    ownershipTransferForm: ComplianceAttachmentLog;
    vaccinationCertificate: AttachmentLog;
    catalogUpdate: AttachmentLog;
    vetStatement: AttachmentLog;
    stableRelease: AttachmentLog;
    paymentsReceived: AttachmentLog;
    hstExemptionForm: AttachmentLog;
}

export interface HorseForSaleOutcome {
    id: number;
    dateCreated: number;
    type: "HAMMER_FALL" | "WITHDRAWAL";
    notes: string | null;
}

export interface HammerFallOutcome extends HorseForSaleOutcome {
    type: "HAMMER_FALL";
    finalBidAmount: number;
    winningBidder: Customer;

    purchasers: Array<HorseForSalePurchase>;
    stableReleaseProvidedTo: Customer;
    winningBidderCreditApplication: CreditApplication | null;
    winningBidderInsuranceRecord: InsuranceRecord | null;
}

export interface WithdrawalOutcome extends HorseForSaleOutcome {
    type: "WITHDRAWAL";
    withVetStatement: boolean;
    fairMarketPrice: number | null;
}

export interface SimpleHorseForSalePurchase {
    id: number | null;

    purchaser: SimpleCustomer;
    percentage: number;
    coveredByWinningBidderCreditAndInsurance: boolean;
    // NOTE: boundBy is filled in automatically by the back-end so it is not specified in requests
    boundBy: AgentAndDelegatorAuthorization | null;
    trainer: boolean;
    notes: string | null;

    hstExemption: {
        exemption: HSTExemptionType;
        notes: string | null;
    };
    creditApplication: CreditApplication | null;
    insuranceRecord: InsuranceRecord | null;

    buyerInvoice: Invoice | null;
}

export interface HorseForSalePurchase extends SimpleHorseForSalePurchase {
    purchaser: Customer;
}



// WEBPACK FOOTER //
// ./src/model/HorseForSale.ts